import { useState, useEffect, useCallback } from "react";
import api, { formatDate, formatCurrency } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, DollarSign, FileText, Users } from "lucide-react";

export default function SalesReportsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/invoices?limit=500"),
      api.get("/quotes?limit=500"),
      api.get("/customers?limit=500"),
    ]).then(([inv, qt, cust]) => {
      const invoices = inv.data.invoices || [];
      const quotes = qt.data.quotes || [];
      const totalRev = invoices.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
      const paidRev = invoices.filter(i => i.status === "Paid").reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
      const outstanding = invoices.filter(i => ["Sent", "Overdue", "Partially Paid"].includes(i.status)).reduce((s, i) => s + (parseFloat(i.balance || i.total) || 0), 0);
      const qtAccepted = quotes.filter(q => q.status === "Accepted").length;
      setData({ totalRev, paidRev, outstanding, invoiceCount: invoices.length, quoteCount: quotes.length, qtAccepted, custCount: (cust.data.customers || []).length,
        topCustomers: Object.entries(invoices.reduce((m, i) => { m[i.customer_name] = (m[i.customer_name] || 0) + (parseFloat(i.total) || 0); return m; }, {})).sort((a, b) => b[1] - a[1]).slice(0, 5),
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="animate-pulse h-60 bg-gray-100 rounded-sm" />;
  if (!data) return null;
  const fmt = v => `R${(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;
  return (
    <div data-testid="sales-reports-page">
      <div className="mb-6"><h1 className="font-heading font-bold text-2xl text-[#1F2937]">Sales Reports</h1></div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[["Total Revenue", fmt(data.totalRev), TrendingUp, "text-blue-600 bg-blue-50"], ["Paid Revenue", fmt(data.paidRev), DollarSign, "text-green-600 bg-green-50"], ["Outstanding", fmt(data.outstanding), FileText, "text-amber-600 bg-amber-50"], ["Customers", data.custCount, Users, "text-purple-600 bg-purple-50"]].map(([t, v, I, c]) => (
          <Card key={t} className="border rounded-sm"><CardContent className="p-4"><div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${c}`}><I className="w-4 h-4" /></div><div className="text-xs text-gray-500 uppercase font-semibold">{t}</div><div className="text-xl font-bold text-[#1F2937] mt-1">{v}</div></CardContent></Card>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Card className="border rounded-sm"><CardContent className="p-4"><h3 className="text-sm font-semibold text-gray-700 mb-3">Quote Conversion</h3><div className="text-3xl font-bold text-[#1F2937]">{data.quoteCount > 0 ? Math.round(data.qtAccepted / data.quoteCount * 100) : 0}%</div><p className="text-xs text-gray-500">{data.qtAccepted} of {data.quoteCount} quotes accepted</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4"><h3 className="text-sm font-semibold text-gray-700 mb-3">Top Customers</h3>
          <div className="space-y-2">{data.topCustomers.map(([name, amt], i) => (
            <div key={i} className="flex items-center justify-between text-sm"><span className="text-gray-700">{name}</span><span className="font-semibold">{fmt(amt)}</span></div>
          ))}</div></CardContent></Card>
      </div>
    </div>
  );
}
